import type { GoogleAdsConfigType } from "./GoogleAdsConfig";

const GTAG_LOAD_TIMEOUT_MS = 2000;

/**
 * Cookies that hold a Google Ads click id. One per click-id parameter, measured
 * rather than assumed:
 *
 * - `gclid` -> `_gcl_aw` (`GCL.<timestamp>.<clickid>`)
 * - `wbraid` -> `_gcl_gb` (`GCL.<timestamp>.<clickid>`)
 * - `gbraid` -> `_gcl_ag` (`2.1.k<clickid>$i<timestamp>`)
 *
 * `gbraid` having its own cookie, in its own format, is the surprise. An
 * earlier version assumed it shared `_gcl_gb` with `wbraid`, which silently
 * dropped every iOS and consent-limited click.
 *
 * Matching the `_gcl_` *prefix* instead would be a correctness bug, not a
 * shortcut: `_gcl_au` holds no click id and is written by the Google Ads tag
 * itself on any page it loads, so a prefix match reads as "came from an ad" for
 * everyone and reports paid installs for merchants who never saw one.
 */
const GOOGLE_CLICK_COOKIE_PATTERN = /(?:^|;\s*)_gcl_(?:aw|gb|ag)=/;

export const hasGoogleClickCookie = (cookieString: string): boolean =>
  GOOGLE_CLICK_COOKIE_PATTERN.test(cookieString);

const gtagLoads = new Map<string, Promise<boolean>>();

export const ensureGtag = (config: GoogleAdsConfigType): Promise<boolean> => {
  const conversionId = config.conversionId;
  const cached = gtagLoads.get(conversionId);

  if (cached) return cached;

  const load = new Promise<boolean>((resolve) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", conversionId);

      const script = document.createElement("script");
      script.async = true;

      const timeout = window.setTimeout(() => {
        gtagLoads.delete(conversionId);
        resolve(false);
      }, GTAG_LOAD_TIMEOUT_MS);

      const settle = (loaded: boolean) => {
        window.clearTimeout(timeout);
        resolve(loaded);
      };

      script.addEventListener("load", () => settle(true));
      script.addEventListener("error", () => settle(false));
      script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
      document.head.appendChild(script);
    } catch {
      resolve(false);
    }
  });

  gtagLoads.set(conversionId, load);

  return load;
};
